import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { createLocalExpirableStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';

export const checkIfUserHasPermissionToAccessConfluence = (cloudId: string) => {
	return fetchJson('/gateway/api/permissions/permitted', {
		method: 'POST',
		body: JSON.stringify({
			resourceId: `ari:cloud:confluence::site/${cloudId}`,
			permissionId: 'write',
		}),
		headers: {
			Accept: 'application/json',
		},
	});
};

const URL = '/gateway/api/permissions/permitted';

const hasProductAccess = async (cloudId: string): Promise<boolean> => {
	const data = {
		permissionId: 'write',
		resourceId: `ari:cloud:confluence::site/${cloudId}`,
	};
	const response = await fetchJson(URL, {
		method: 'POST',
		body: JSON.stringify(data),
	});
	return response?.permitted;
};

const CONFLUENCE_ACCESS_CACHE_KEY = 'confluence-reactivation-exp.confluence-access.cache.key';
// Cache for 1 day
const CONFLUENCE_ACCESS_CACHE_KEY_EXPIRY_MS = 24 * 60 * 60 * 1000;
const confluenceAccessApiCache = createLocalExpirableStorageProvider(
	'confluence-reactivation-exp-access-api-cache',
);

export const fetchCachedConfluenceAccess = async (
	cloudId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<Boolean | null> => {
	let confluenceAccess = confluenceAccessApiCache.get(CONFLUENCE_ACCESS_CACHE_KEY);

	if (confluenceAccess === undefined) {
		try {
			confluenceAccess = await hasProductAccess(cloudId);
		} catch (error) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'confReactivationInSearchExpConfAccess failed',
				{
					errorAction: 'Failed to get confluence access',
					error,
				},
			);
		} finally {
			// Set the cache either way. This make sure if error occurs, we don't keep hitting the API.
			setConfluenceAccessCache(confluenceAccess ?? false, createAnalyticsEvent);
		}
	}

	return confluenceAccess ?? false;
};

export const setConfluenceAccessCache = (
	value: boolean,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	try {
		confluenceAccessApiCache.set(
			CONFLUENCE_ACCESS_CACHE_KEY,
			value,
			Date.now() + CONFLUENCE_ACCESS_CACHE_KEY_EXPIRY_MS,
		);
	} catch (error) {
		fireOperationalAnalytics(
			createAnalyticsEvent({}),
			'confReactivationInSearchExpConfAccess failed',
			{
				errorAction: 'Failed to set confluence access in cache',
				error,
			},
		);
	}
};
